import { NgModule, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from './loader/loader.component';
import { GridLoaderComponent } from './grid/grid-loader/grid-loader.component';
import { ConfigServiceProvider } from './services/config.service';
import { AgGridAngular } from 'ag-grid-angular';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { PhoneNumberPipe } from 'src/shared/phone-number.pipe/phone-number.pipe';
import { SSNPipe } from './ssn.pipe/ssn.pipe';
import { NumericOnlyDirective } from './directives/numeric-only.directive';
import { ValidationErrorsDirective } from './utilities/validation-errors.directive';
import { ValidationSummaryComponent } from './validation-summary/validation-summary.component';
import { ComponentRequiresPolicyDirective } from './directives/component-requires-policy.directive';
import { ComponentRequiresAnyPolicyDirective } from './directives/component-requires-any-policy.directive';
import { DatePickerComponent } from './date-picker/date-picker.component';
import { NgbCollapseModule, NgbModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { AuditPopoverComponent } from './audit-popover/audit-popover.component';
import { FlatpickrModule } from 'angularx-flatpickr';
import { FormLevelValidationErrorsDirective } from './utilities/form-level-validation-errors.directive';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { TooltipDirective } from './directives/tooltip.directive';
import { CurrencyDirective } from './directives/currency.directive';
import { IconLinkRendererComponent } from './grid/icon-link-renderer/icon-link-renderer.component';
import { ScannerComponent } from './scanner/scanner.component';
import { MonthYearPickerComponent } from './month-year-picker/month-year-picker.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OrderByPipe } from './order-by.pipe/order-by.pipe';
import { IsLinkedIconRendererComponent } from './grid/is-linked-icon-renderer/is-linked-icon-renderer.component';
import { UploaderComponent } from './uploader/uploader.component';
import { DocumentViewerComponent } from './document-viewer/document-viewer.component';
import { DocumentReviewEducatorDetailsComponent } from './document-review-educator-details/document-review-educator-details.component';
import { ScannerLauncherComponent } from './scanner/scanner-launcher/scanner-launcher.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { GridValidationErrorsDirective } from './utilities/grid-validation.directive';
import { FileSystemDirective } from './directives/file-system.directive';
import { CustomSelectRendererComponent } from './grid/custom-select-renderer/custom-select-renderer.component';
import { CustomButtonRendererComponent } from './grid/custom-button-renderer/custom-button-renderer.component';
import { EducatorHeaderComponent } from './educator-header/educator-header.component';
import { AddAlertModalComponent } from './educator-header/add-alert-modal/add-alert-modal.component';
import { EducatorNotesComponent } from './educator-header/educator-notes/educator-notes.component';
import { EducatorNotesListComponent } from './educator-header/educator-notes/educator-notes-list/educator-notes-list.component';
import { MARKED_OPTIONS, MarkdownModule, SANITIZE } from 'ngx-markdown';
import { markedOptionsFactory } from './markdown-options/markdown-factories';
import { NgRouteRendererComponent } from './grid/ng-route-renderer/ng-route-renderer.component';
import { NgxMaskPipe, NgxMaskDirective, provideEnvironmentNgxMask } from 'ngx-mask';
import { DateInputComponent } from './date-input/date-input.component';
import { BadgeCollectionRendererComponent } from './grid/badge-collection-renderer/badge-collection-renderer.component';
import { DateTimeInputComponent } from './date-time-input/date-time-input.component';
import { PdRequirementStateComponent } from './pd-requirement-state/pd-requirement-state.component';
import { DotsLoadingComponent } from './dots-loading/dots-loading.component';
import { ObscurityDirective } from './directives/obscurity.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ObscuredAgGridCellRendererComponent } from './obscured-ag-grid-cell-renderer/obscured-ag-grid-cell-renderer.component';
import { BadgeCellRendererComponent } from './badge-cell-renderer/badge-cell-renderer.component';
import { LinkedEducatorHeaderModalComponent } from './educator-header/linked-educator-header-modal/linked-educator-header-modal.component';
import { LinkedEducatorIconComponent } from './linked-educator-icon/linked-educator-icon.component';
import { LinkedEducatorDescriptionComponent } from './linked-educator-description/linked-educator-description.component';
import { GuidFormatPipe } from './guid-format.pipe';
import { MergeConfirmationModalComponent } from './merge-confirmation-modal/merge-confirmation-modal.component';
import { TestStatusBadgePipe } from './test-status-badge.pipe/test-status-badge.pipe';
import { EnumNamePipe } from './enum-name.pipe/enum-name-pipe';
import { AccessBlockedComponent } from './access-blocked/access-blocked.component';
import { SpecialProgramBadgeComponent } from './special-program-badge/special-program-badge.component';
import { QualificationPathBadgeComponent } from './qualification-path-badge/qualification-path-badge.component';
import { DecimalDirective } from './directives/decimal.directive';
import { RequiredInputDirective } from './directives/required-input.directive';
import { MismatchedFingerprintAssociationPopoverComponent } from './mismatched-fingerprint-association-popover/mismatched-fingerprint-association-popover.component';
import { RushIconComponent } from './rush-icon/rush-icon.component';
import { ConfirmInputModalComponent } from './confirm-input-modal/confirm-input-modal.component';
import { VoucherAllocationResultsComponent } from './vouchers/voucher-allocation-results.component';
import { VoucherPersonSearchComponent } from './vouchers/voucher-person-search.component';
import { VoucherPersonBucketComponent } from './vouchers/voucher-person-bucket.component';
import { VoucherCreateCreditsFormComponent } from './vouchers/voucher-create-credits-form.component';
import { VoucherCreditsGridComponent } from './vouchers/voucher-credits-grid.component';
import { VoucherInfoBannerComponent } from './vouchers/voucher-info-banner.component';
import { UpdateVoucherCreditsModalComponent } from './vouchers/update-voucher-credits-modal/update-voucher-credits-modal.component';
import { PdVerificationRequestModalComponent } from './professional-development/pd-verification-request-modal/pd-verification-request-modal.component';
import { PdSendLeaRequestsFormComponent } from './professional-development/pd-send-lea-requests-form/pd-send-lea-requests-form.component';
import { PdPersonSearchComponent } from './professional-development/pd-person-search/pd-person-search.component';
import { LeaRushContactModalComponent } from './lea-rush-contact-modal/lea-rush-contact-modal.component';
import { DistributionCycleCardComponent } from './vouchers/distribution-cycle-card/distribution-cycle-card.component';
import { CycleColorPipe } from './vouchers/cycle-color.pipe';
import { VoucherAllocationCardComponent } from './vouchers/voucher-allocation-card/voucher-allocation-card.component';
import { ProfessionalDevelopmentAwardFormComponent } from './professional-development/pd-award-form/pd-award-form.component';
import { ProfessionalDevelopmentRecentAwardsGridComponent } from './professional-development/pd-recent-awards-grid/pd-recent-awards-grid.component';
import { ProfessionalDevelopmentRecentAwardsActionsCellRenderer } from './professional-development/pd-recent-awards-grid/pd-recent-awards-actions-cell-renderer/actions-cell/pd-recent-awards-actions-cell-renderer.component';
import { UpdateProfessionalDevelopmentModalComponent } from './professional-development/update-pd-modal/update-pd-modal.component';
import { AwardProfessionalDevelopmentModalComponent } from './professional-development/award-pd-modal/award-pd-modal.component';
import { PDAdvancedPersonSearchComponent } from './professional-development/pd-advanced-person-search/pd-advanced-person-search.component';
import { BulkEinFormatterModalComponent } from './professional-development/pd-advanced-person-search/bulk-ein-formatter-modal/bulk-ein-formatter-modal.component';
import { ProfessionalDevelopmentAdvancedPersonSearchResultsGridComponent } from './professional-development/pd-advanced-person-search/pd-advanced-person-search-results-grid/pd-advanced-person-search-results-grid.component';
import { ProfessionalDevelopmentAdvancedPersonSearchActionsCellRenderer } from './professional-development/pd-advanced-person-search/pd-advanced-person-search-results-grid/pd-advanced-person-search-action-cell-renderer/pd-advanced-person-search-actions-cell-renderer.component';
import { VerifyRemainingProfessionalDevelopmentModalComponent } from './professional-development/verify-remaining-pd-modal/verify-remaining-pd-modal.component';
import { ComponentRequiresAllPoliciesDirective } from './directives/component-requires-all-policies.directive';
import { PageAlertBannerComponent } from './page-alert-banner/page-alert-banner.component';

@NgModule({
  declarations: [
    ValidationSummaryComponent,
    LoaderComponent,
    ConfirmationModalComponent,
    PhoneNumberPipe,
    SSNPipe,
    NumericOnlyDirective,
    DecimalDirective,
    RequiredInputDirective,
    ValidationErrorsDirective,
    DatePickerComponent,
    DateRangePickerComponent,
    AuditPopoverComponent,
    FormLevelValidationErrorsDirective,
    ComponentRequiresPolicyDirective,
    ComponentRequiresAnyPolicyDirective,
    ComponentRequiresAllPoliciesDirective,
    TooltipDirective,
    IconLinkRendererComponent,
    ScannerComponent,
    CurrencyDirective,
    MonthYearPickerComponent,
    OrderByPipe,
    GridLoaderComponent,
    IsLinkedIconRendererComponent,
    UploaderComponent,
    DocumentViewerComponent,
    DocumentReviewEducatorDetailsComponent,
    ScannerLauncherComponent,
    GridValidationErrorsDirective,
    FileSystemDirective,
    CustomSelectRendererComponent,
    CustomButtonRendererComponent,
    PageAlertBannerComponent,
    EducatorHeaderComponent,
    AddAlertModalComponent,
    EducatorNotesComponent,
    EducatorNotesListComponent,
    DateInputComponent,
    NgRouteRendererComponent,
    BadgeCollectionRendererComponent,
    DateTimeInputComponent,
    PdRequirementStateComponent,
    DotsLoadingComponent,
    ObscurityDirective,
    AutofocusDirective,
    ObscuredAgGridCellRendererComponent,
    BadgeCellRendererComponent,
    LinkedEducatorHeaderModalComponent,
    LinkedEducatorIconComponent,
    LinkedEducatorDescriptionComponent,
    GuidFormatPipe,
    MergeConfirmationModalComponent,
    TestStatusBadgePipe,
    EnumNamePipe,
    AccessBlockedComponent,
    SpecialProgramBadgeComponent,
    QualificationPathBadgeComponent,
    MismatchedFingerprintAssociationPopoverComponent,
    ConfirmInputModalComponent,
    RushIconComponent,
    VoucherAllocationResultsComponent,
    VoucherPersonSearchComponent,
    VoucherPersonBucketComponent,
    VoucherCreateCreditsFormComponent,
    VoucherCreditsGridComponent,
    VoucherInfoBannerComponent,
    UpdateVoucherCreditsModalComponent,
    PdVerificationRequestModalComponent,
    PdSendLeaRequestsFormComponent,
    PdPersonSearchComponent,
    LeaRushContactModalComponent,
    DistributionCycleCardComponent,
    CycleColorPipe,
    VoucherAllocationCardComponent,
    ProfessionalDevelopmentAwardFormComponent,
    ProfessionalDevelopmentRecentAwardsGridComponent,
    ProfessionalDevelopmentRecentAwardsActionsCellRenderer,
    UpdateProfessionalDevelopmentModalComponent,
    AwardProfessionalDevelopmentModalComponent,
    PDAdvancedPersonSearchComponent,
    BulkEinFormatterModalComponent,
    ProfessionalDevelopmentAdvancedPersonSearchResultsGridComponent,
    ProfessionalDevelopmentAdvancedPersonSearchActionsCellRenderer,
    VerifyRemainingProfessionalDevelopmentModalComponent,
  ],
  imports: [
    CommonModule,
    NgxSkeletonLoaderModule.forRoot({
      animation: 'progress',
      loadingText: 'Loading...',
      ariaLabel: 'Loading',
    }),
    MarkdownModule.forRoot({
      sanitize: {
        provide: SANITIZE,
        useValue: SecurityContext.HTML,
      },
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    AgGridAngular,
    RouterModule,
    InfiniteScrollModule,
    NgSelectModule,
    FormsModule,
    NgbModule,
    FlatpickrModule.forRoot(),
    NgbTooltipModule,
    NgbCollapseModule,
    DragDropModule,
    NgxExtendedPdfViewerModule,
    NgxMaskPipe,
    NgxMaskDirective,
  ],
  exports: [
    ValidationSummaryComponent,
    NgxSkeletonLoaderModule,
    AgGridAngular,
    InfiniteScrollModule,
    NgSelectModule,
    FormsModule,
    NgbModule,
    PhoneNumberPipe,
    SSNPipe,
    NumericOnlyDirective,
    DecimalDirective,
    RequiredInputDirective,
    ValidationErrorsDirective,
    FormLevelValidationErrorsDirective,
    ComponentRequiresPolicyDirective,
    ComponentRequiresAnyPolicyDirective,
    ComponentRequiresAllPoliciesDirective,
    DatePickerComponent,
    DateRangePickerComponent,
    AuditPopoverComponent,
    FlatpickrModule,
    NgbTooltipModule,
    DatePipe,
    CurrencyPipe,
    TooltipDirective,
    NgbCollapseModule,
    ScannerComponent,
    CurrencyDirective,
    LoaderComponent,
    GridLoaderComponent,
    MonthYearPickerComponent,
    DragDropModule,
    OrderByPipe,
    UploaderComponent,
    DocumentViewerComponent,
    DocumentReviewEducatorDetailsComponent,
    ScannerLauncherComponent,
    GridValidationErrorsDirective,
    FileSystemDirective,
    EducatorHeaderComponent,
    NgxMaskPipe,
    NgxMaskDirective,
    DateInputComponent,
    DateTimeInputComponent,
    MarkdownModule,
    PdRequirementStateComponent,
    DotsLoadingComponent,
    ObscurityDirective,
    AutofocusDirective,
    ObscuredAgGridCellRendererComponent,
    BadgeCellRendererComponent,
    NgbPopoverModule,
    LinkedEducatorIconComponent,
    LinkedEducatorDescriptionComponent,
    GuidFormatPipe,
    TestStatusBadgePipe,
    EnumNamePipe,
    AccessBlockedComponent,
    SpecialProgramBadgeComponent,
    MismatchedFingerprintAssociationPopoverComponent,
    ConfirmInputModalComponent,
    RushIconComponent,
    VoucherAllocationResultsComponent,
    VoucherPersonSearchComponent,
    VoucherPersonBucketComponent,
    VoucherCreateCreditsFormComponent,
    VoucherCreditsGridComponent,
    VoucherInfoBannerComponent,
    UpdateVoucherCreditsModalComponent,
    PdVerificationRequestModalComponent,
    PdSendLeaRequestsFormComponent,
    DistributionCycleCardComponent,
    CycleColorPipe,
    VoucherAllocationCardComponent,
    PdPersonSearchComponent,
    ProfessionalDevelopmentAwardFormComponent,
    ProfessionalDevelopmentRecentAwardsGridComponent,
    AwardProfessionalDevelopmentModalComponent,
    PDAdvancedPersonSearchComponent,
    BulkEinFormatterModalComponent,
    PageAlertBannerComponent,
  ],
  providers: [ConfigServiceProvider, DatePipe, CurrencyPipe, provideEnvironmentNgxMask()],
})
export class SharedModule {}
